import React from 'react';
import { Loader2 } from 'lucide-react';
import { FOCUS } from './tokens';

/**
 * The one button.
 *
 * Before 2026-09-10 each page styled its own. Primary actions came in four blues (brand, blue-700,
 * blue-600, blue-500) plus an emerald, 28 to 48px tall, with labels from 11 to 16px, and hover went
 * darker, lighter, faded or did nothing. docs/DESIGN.md has the rules this encodes: one `primary`
 * per screen, sentence-case labels, 36px tall (32px for `sm`).
 */

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'md' | 'sm';

const VARIANT: Record<Variant, string> = {
  primary: 'border border-primary-500 bg-primary-500 text-white hover:border-primary-600 hover:bg-primary-600',
  secondary: 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900',
  ghost: 'border border-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-900',
  danger: 'border border-red-200 bg-white text-red-700 hover:bg-red-50',
};

/**
 * An icon-only destructive button (a row's delete) is a borderless red icon, not a red box per row.
 *
 * Six callers used to get this by passing red classes over `ghost`. A class string does not decide
 * which colour wins, the stylesheet's order does, and `.text-slate-600` is emitted after
 * `.text-red-600`. So every one of those delete icons rendered grey (measured on QA, 2026-09-14).
 */
const DANGER_ICON = 'border border-transparent text-red-600 hover:bg-red-50 hover:text-red-700';

const SIZE: Record<Size, { labelled: string; iconOnly: string }> = {
  md: { labelled: 'h-9 px-4 text-sm', iconOnly: 'h-9 w-9' },
  sm: { labelled: 'h-8 px-3 text-xs', iconOnly: 'h-8 w-8' },
};

type BaseProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & {
  variant?: Variant;
  size?: Size;
  /** Disables the button and swaps its icon for a spinner while an action is in flight. */
  loading?: boolean;
  /** Drawn before the label, or alone on an icon-only button. Give the icon `aria-hidden`. */
  icon?: React.ReactNode;
};

type LabelledButtonProps = BaseProps & { children: React.ReactNode };

/** With no visible text, the button's name has to come from somewhere a screen reader can read. */
type IconOnlyButtonProps = BaseProps & { children?: undefined; icon: React.ReactNode; 'aria-label': string };

export type ButtonProps = LabelledButtonProps | IconOnlyButtonProps;

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading = false, icon, children, className = '', disabled, type = 'button', ...rest },
  ref
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={[
        'inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md font-semibold transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-50',
        children == null ? SIZE[size].iconOnly : SIZE[size].labelled,
        children == null && variant === 'danger' ? DANGER_ICON : VARIANT[variant],
        FOCUS,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {loading ? <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin motion-reduce:animate-none" /> : icon}
      {children}
    </button>
  );
});

export default Button;
